import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FiX, FiCalendar, FiChevronDown } from "react-icons/fi";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import "./SearchFilterModal.scss";

interface SearchFilterModalProps {
  show: boolean;
  onHide: () => void;
  onApplyFilters?: (filters: FilterState) => void;
  initialFilters?: FilterState;
}

interface FilterState {
  province: string[];
  city: string;
  startDate: string;
  endDate: string;
}

const SearchFilterModal: React.FC<SearchFilterModalProps> = ({
  show,
  onHide,
  onApplyFilters,
  initialFilters,
}) => {
  const [filters, setFilters] = useState<FilterState>({
    province: [],
    city: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    if (show && initialFilters) {
      setFilters(initialFilters);
    }
  }, [show, initialFilters]);

  const provinces = [
    "Alberta",
    "Nova Scotia",
    "British Columbia",
    "Ontario",
    "Manitoba",
    "Prince Edward Island",
    "New Brunswick",
    "Quebec",
    "Newfoundland and Labrador",
    "Saskatchewan",
  ];

  const handleProvinceToggle = (province: string) => {
    setFilters((prev) => ({
      ...prev,
      province: prev.province.includes(province)
        ? prev.province.filter((p) => p !== province)
        : [...prev.province, province],
    }));
  };

  const handleReset = () => {
    setFilters({
      province: [],
      city: "",
      startDate: "",
      endDate: "",
    });
  };

  const handleDone = () => {
    if (onApplyFilters) {
      onApplyFilters(filters);
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered className="search-filter-modal">
      <Modal.Header>
        <Modal.Title>Search filter</Modal.Title>
        <button className="close-button" onClick={onHide}>
          <FiX />
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="filter-section">
          <label className="filter-label">Province</label>
          <div className="checkbox-grid">
            {provinces.map((province, index) => (
              <div key={province} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`province-${index}`}
                  checked={filters.province.includes(province)}
                  onChange={() => handleProvinceToggle(province)}
                />
                <label htmlFor={`province-${index}`}>{province}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <label className="filter-label">City</label>
          <div className="select-wrapper">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Select city"
              options={[
                { value: "toronto", label: "Toronto" },
                { value: "montreal", label: "Montreal" },
                { value: "vancouver", label: "Vancouver" },
                { value: "calgary", label: "Calgary" },
                { value: "ottawa", label: "Ottawa" },
              ]}
              value={
                filters.city
                  ? {
                      value: filters.city,
                      label:
                        filters.city.charAt(0).toUpperCase() +
                        filters.city.slice(1),
                    }
                  : null
              }
              onChange={(option: any) =>
                setFilters((prev) => ({ ...prev, city: option?.value || "" }))
              }
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? "#00A759"
                    : state.isFocused
                      ? "rgba(0, 167, 89, 0.1)"
                      : "white",
                  color: state.isSelected ? "white" : "#1e293b",
                  "&:active": {
                    backgroundColor: "#00A759",
                    color: "white",
                  },
                }),
              }}
              components={{
                DropdownIndicator: () => (
                  <FiChevronDown className="select-icon" />
                ),
                IndicatorSeparator: () => null,
              }}
            />
          </div>
        </div>

        <div className="filter-section">
          <label className="filter-label">Date range</label>
          <div className="date-input-wrapper">
            <DatePicker
              selectsRange={true}
              startDate={
                filters.startDate ? new Date(filters.startDate) : undefined
              }
              endDate={filters.endDate ? new Date(filters.endDate) : undefined}
              onChange={(update: [Date | null, Date | null]) => {
                const [start, end] = update;
                setFilters((prev) => ({
                  ...prev,
                  startDate: start ? start.toISOString() : "",
                  endDate: end ? end.toISOString() : "",
                }));
              }}
              placeholderText="Select date range"
              className="form-control"
              dateFormat="MMMM d, yyyy"
            />
            <FiCalendar className="date-icon" />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-outline" onClick={handleReset}>
          Reset changes
        </button>
        <button className="btn btn-primary" onClick={handleDone}>
          Done
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchFilterModal;
